import * as React from 'react';
import classnames from 'classnames';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import PopoverList from 'components/popover-list/PopoverList';

import SubcampaignOfferActions from 'modules/subcampaigns/components/SubcampaignOfferActions';
import OfferBannerContext from './context/OfferBannerContext';
import OfferBannerRevoked from './revoked/OfferBannerRevoked';
import OfferBannerQuote from './quote/OfferBannerQuote';
import OfferBannerRenegotiation from './renegotiation/OfferBannerRenegotiation';
import OfferBannerPending from './pending/OfferBannerPending';
import OfferBannerAccepted from './accepted/OfferBannerAccepted';

import { __t, applyTranslations } from 'utils/language';
import { isInfluencerProfileRejected } from 'utils/profile';
import { sanitizeGenericHtml } from 'utils/sanitize';
import { stripHTML } from 'utils/helper/common';
import {
  isAccepted,
  isExpired,
  isExpiredAsCampaignEnded,
  isNegotiationsReopened,
  isPending,
  isQuote,
  isQuoteProvided as isQuoteProvidedByCreator,
  isRevoked
} from 'utils/subcampaign';

import './offer-banner.scss';

applyTranslations(require('./offer-banner.i18n.json'));

import type {
  Subcampaign,
  SubcampaignInfluencerProfile,
  SubcampaignOffer
} from 'types';

import type {
  OfferBannerContext as OfferBannerContextAlias,
  OfferBannerRevokedBy,
  OfferBannerUserType
} from './types';

type Props = {
  subcampaign: Subcampaign,
  brandProfileName: string,
  isBrand: boolean,
  setProvideQuoteClick: (provideQuoteClick: () => void) => void,
  onBrandEditOfferAfterReceivingQuote?: () => void
};

const MSG_KEY = 'offer_banner';

const OfferBanner: React.FC<Props> = ({
  subcampaign,
  brandProfileName,
  isBrand,
  setProvideQuoteClick,
  onBrandEditOfferAfterReceivingQuote
}: Props) => {
  const userType: OfferBannerUserType = isBrand ? 'brand' : 'creator';
  const campaignId = subcampaign.campaign.id;
  const subcampaignId = subcampaign.id;
  const offer = get(subcampaign, 'offer', {}) as SubcampaignOffer;
  const updatedOffer = get(subcampaign, 'updatedOffer', {}) as SubcampaignOffer;
  const creatorProfile = get(subcampaign, 'creatorProfile', {}) as SubcampaignInfluencerProfile;
  const isSubcampaignRevoked = isRevoked(subcampaign);
  const isNewOffer = offer?.state === 'pending' && isEmpty(updatedOffer);
  const isRenegotiation = !isNewOffer && !isEmpty(updatedOffer);
  const isQuoteProvided = offer?.type === 'quote' && isQuoteProvidedByCreator(subcampaign);
  let subcampaignExpirationType = '';

  if (isExpired(subcampaign)) {
    subcampaignExpirationType = 'expired';
  }

  if (isExpiredAsCampaignEnded(subcampaign)) {
    subcampaignExpirationType = 'campaign_ended';
  }

  const renderPopover = (): React.ReactNode => {
    const popoverDetails = {
      title: __t(`${MSG_KEY}.labels.offer_expiration`),
      body: __t(`${MSG_KEY}.${userType}.popover`)
    };

    return (
      <PopoverList
        list={[popoverDetails]}
        lineHeight="normal"
        classNamePopover="offer-banner__popover"
      />
    );
  };

  const renderPrimaryText = (
    text: string
  ): React.ReactNode => {
    if (isEmpty(text)) {
      return null;
    }

    return (
      <div
        className="offer-banner__primary-text"
        title={text}
      >
        {text}
      </div>
    );
  };

  const renderSecondaryText = (
    text: string
  ): React.ReactNode => {
    if (isEmpty(text)) {
      return null;
    }

    return (
      <div
        className="offer-banner__secondary-text"
        title={stripHTML(text)}
        dangerouslySetInnerHTML={{ __html: sanitizeGenericHtml(text) }}
      />
    );
  };

  const renderGenericText = (
    text: string
  ): React.ReactNode => {
    if (isEmpty(text)) {
      return null;
    }

    return (
      <div
        className="offer-banner__generic-text"
        title={stripHTML(text)}
        dangerouslySetInnerHTML={{ __html: sanitizeGenericHtml(text) }}
      />
    );
  };

  const renderStatus = (
    text: string
  ): React.ReactNode => {
    if (isEmpty(text)) {
      return null;
    }

    const state = isNegotiationsReopened(subcampaign)
      ? 'pending'
      : subcampaign.state;

    const statusClasses = classnames('offer-banner__status', {
      [`offer-banner__status--${state}`]: true
    });

    return (
      <div className={statusClasses}>
        {text}
      </div>
    );
  };

  const renderDisclaimer = (msgKey: string): React.ReactNode => {
    const text = __t(`${MSG_KEY}.${userType}.disclaimers.${msgKey}`);

    return (
      <div
        className="offer-banner__disclaimer"
        title={text}
      >
        {text}
        {renderPopover()}
      </div>
    );
  };

  const renderCreatorActions = (): React.ReactNode => {
    const isInfluencerRejected = isInfluencerProfileRejected(creatorProfile);

    if (isBrand || isInfluencerRejected) {
      return null;
    }

    return (
      <SubcampaignOfferActions
        setProvideQuoteClick={(provideQuoteClick) => { setProvideQuoteClick(provideQuoteClick); }}
        buttonWidth="normal"
        subcampaign={subcampaign}
      />
    );
  };

  const renderContents = (): React.ReactNode => {
    if (isSubcampaignRevoked) {
      return (
        <OfferBannerRevoked
          revokedBy={subcampaign.revokedBy as OfferBannerRevokedBy}
        />
      );
    }

    if (isNegotiationsReopened(subcampaign)) {
      return <OfferBannerRenegotiation />;
    }

    if (isQuote(subcampaign)) {
      return <OfferBannerQuote />;
    }

    if (isPending(subcampaign)) {
      return <OfferBannerPending />;
    }

    if (isAccepted(subcampaign)) {
      return <OfferBannerAccepted />;
    }

    return null;
  };

  const context = React.useMemo<OfferBannerContextAlias>(() => ({
    userType,
    campaignId,
    subcampaignId,
    offer,
    updatedOffer,
    creatorProfile,
    brandProfileName,
    subcampaignExpirationType,
    isSubcampaignRevoked,
    isNewOffer,
    isRenegotiation,
    isQuoteProvided,
    isBrand,
    renderPrimaryText,
    renderSecondaryText,
    renderGenericText,
    renderStatus,
    renderDisclaimer,
    renderCreatorActions,
    onBrandEditOfferAfterReceivingQuote
  }), [
    userType,
    campaignId,
    subcampaignId,
    offer,
    updatedOffer,
    creatorProfile,
    brandProfileName,
    subcampaignExpirationType,
    isSubcampaignRevoked,
    isNewOffer,
    isRenegotiation,
    isQuoteProvided,
    isBrand
  ]);

  return (
    <OfferBannerContext.Provider value={context}>
      <div className="offer-banner">
        {renderContents()}
      </div>
    </OfferBannerContext.Provider>
  );
};

export default OfferBanner;
